/**
 * TicketDashboard constants — shared across all ticket components.
 */

export const ALLOWED_ROLES = ['owner', 'admin', 'manager'];
export const STALE_MINUTES = 90;
// The live board's Completed tab shows only the last few hours of finished
// orders; the whole day is in the History tab. Open/stale tickets are never trimmed.
export const KDS_COMPLETED_WINDOW_MIN = 180;
export const DELAY_THRESHOLD_MIN = 17;

// Order Alerts: only surface open misc tickets above this revenue (cents). Tiny
// orders (taxes/rounding/empty tickets) aren't worth chasing a reason for.
export const OA_MIN_REVENUE_CENTS = 500;

// ── Kitchen-load Telegram alerts ──────────────────────────────────────────────
// A remote Telegram alert interrupts the owner when they're NOT in the kitchen,
// so it must be RARE and HIGH-SIGNAL: it fires only when the kitchen is
// genuinely OVERWHELMED as a whole, never for a single slow order (those are
// surfaced in-app by the on-screen color/sound for the staff who are present).
//
// One level only — CRITICAL (= paging). It is "aggregate overload":
//   • 2+ delayed tickets AND a real backlog (20+ unmade items), OR
//   • 5+ delayed tickets, OR
//   • a huge backlog (40+ unmade items).
// A lone very-late order, or merely being busy, will NOT trip it.
//
//   enter   → cross into CRITICAL when ANY of these aggregate rules hold
//   clear   → stay CRITICAL until BELOW all of the (relaxed) clear rules — a
//             hysteresis deadband so it can't flap
//   sustainUpMs   → must hold ~3 min before paging (a brief rush burst won't)
//   heartbeatMs   → while still critical, re-ping at most this often
export const KDS_LOAD_ALERT_CONFIG = {
  ticketDelayMin: 15,                                       // a ticket is "delayed" at this age
  enter: { comboDelayed: 2, comboBacklog: 20, delayedAlone: 5, backlogAlone: 40 },
  clear: { comboDelayed: 2, comboBacklog: 14, delayedAlone: 4, backlogAlone: 30 },
  sustainUpMs:   3 * 60 * 1000,
  sustainDownMs: 2 * 60 * 1000,
  heartbeatMs:   15 * 60 * 1000,
  // Cross-device dedup windows. When several tabs/devices fire "the same" alert,
  // only the first to claim a shared Firestore slot within the window sends.
  dedup: {
    escalateMs:  4 * 60 * 1000,   // > device spread (~sustainUp), < gap to a new incident
    heartbeatMs: 14 * 60 * 1000,  // < heartbeat interval (15m) so each beat still fires once
    recoveredMs: 3 * 60 * 1000,
  },
};

export const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : 'https://my-app-ramakrishna5s-projects.vercel.app';

// Notifications always go to production (has the keys) even in local dev
export const NOTIFY_API_BASE = 'https://my-app-ramakrishna5s-projects.vercel.app';

export const KDS_CACHE_KEY = 'kds_orders_cache';

// Auto mode's backup poll once this board has received a webhook signal (the
// push is proven to work). Until then, and in any pinned mode, the poll keeps
// its normal interval — a broken webhook never slows new orders down.
export const KDS_RELAXED_POLL_MS = 30000;

export const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '10s', value: 10000 },
  { label: '15s', value: 15000 },
  { label: '20s', value: 20000 },
  { label: '30s', value: 30000 },
  { label: '60s', value: 60000 },
];

export const DURATION_BUCKETS = [
  { label: '<5m', min: 0, max: 5, color: '#10b981' },
  { label: '5–10m', min: 5, max: 10, color: '#22c55e' },
  { label: '10–20m', min: 10, max: 20, color: '#f59e0b' },
  { label: '20–30m', min: 20, max: 30, color: '#f97316' },
  { label: '30–60m', min: 30, max: 60, color: '#ef4444' },
  { label: '>60m', min: 60, max: Infinity, color: '#991b1b' },
];

export const GAP_BUCKETS = [
  { label: '0–2m', min: 0, max: 2, color: '#10b981' },
  { label: '2–5m', min: 2, max: 5, color: '#22c55e' },
  { label: '5–10m', min: 5, max: 10, color: '#f59e0b' },
  { label: '10–15m', min: 10, max: 15, color: '#f97316' },
  { label: '15–30m', min: 15, max: 30, color: '#ef4444' },
  { label: '>30m', min: 30, max: Infinity, color: '#7f1d1d' },
];

// Completion-time buckets for the History tab (minutes to final completion).
// Ranges are [min, max). Click a bucket to filter the order table to it.
export const COMPLETION_BUCKETS = [
  { key: 'u15',   label: '< 15m',  min: 0,  max: 15,       color: '#10b981' },
  { key: '15_17', label: '15–17m', min: 15, max: 17,       color: '#22c55e' },
  { key: '17_20', label: '17–20m', min: 17, max: 20,       color: '#f59e0b' },
  { key: '20_30', label: '20–30m', min: 20, max: 30,       color: '#f97316' },
  { key: '30p',   label: '30m+',   min: 30, max: Infinity, color: '#ef4444' },
];

export const DURATION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'fast', label: '< 10m', test: m => m < 10 },
  { key: 'ok', label: '10–20m', test: m => m >= 10 && m < 20 },
  { key: 'slow', label: '> 20m', test: m => m >= 20 },
  { key: 'long', label: '> 30m', test: m => m >= 30 },
];

export const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'custom', label: 'Custom' },
];

// ─── Quality tagging ──────────────────────────────────────────────────────────
// Flags an order can be tagged with during or after service. `bad: true` flags
// count against the Service Quality Score; `weight` sets how heavily each hurts
// (a complaint stings more than a single remake). `praise` is positive feedback
// and never lowers the score.
export const QUALITY_FLAGS = [
  { key: 'remake',    label: 'Remake',        emoji: '🔁', bad: true,  weight: 1.0, hint: 'Item had to be remade' },
  { key: 'wrong',     label: 'Wrong Item',    emoji: '❌', bad: true,  weight: 1.0, hint: 'Wrong item made or handed out' },
  { key: 'complaint', label: 'Complaint',     emoji: '😞', bad: true,  weight: 1.5, hint: 'Customer complained' },
  { key: 'quality',   label: 'Food Quality',  emoji: '⚠️', bad: true,  weight: 1.5, hint: 'Taste / temperature / presentation issue' },
  { key: 'late',      label: 'Late',          emoji: '⏰', bad: true,  weight: 0.5, hint: 'Order ran late' },
  { key: 'comp',      label: 'Comp / Refund', emoji: '💸', bad: true,  weight: 1.0, hint: 'Comped, voided, or refunded' },
  { key: 'praise',    label: 'Praise',        emoji: '💚', bad: false, weight: 0.0, hint: 'Customer praised the order' },
];

// Component weights for the 0–100 Service Quality Score (should sum to 1).
export const QUALITY_SCORE_WEIGHTS = { onTime: 0.40, defectFree: 0.45, notStale: 0.15 };

export const STATUS_MAP = {
  open: { label: 'Open', cls: 'td__badge--open' },
  inprogress: { label: 'In Progress', cls: 'td__badge--inprogress' },
  completed: { label: 'Done', cls: 'td__badge--completed' },
  cancelled: { label: 'Cancelled', cls: 'td__badge--cancelled' },
  stale: { label: 'Stale', cls: 'td__badge--stale' },
};

export const CHEF_PROFILE_COLORS = [
  '#ff6b00', '#ff2d55', '#ff3cac', '#a855f7', '#3b82f6', '#06b6d4', '#10b981', '#84cc16',
  '#facc15', '#f97316', '#e11d48', '#7c3aed', '#0ea5e9', '#14b8a6', '#22c55e', '#eab308',
];

export const CAT_PIE_COLORS = [
  '#d97706', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899',
  '#14b8a6', '#84cc16', '#f97316', '#a855f7', '#06b6d4', '#eab308',
  '#e879f9', '#34d399', '#fb923c', '#60a5fa',
];

export const CAT_COLORS = [
  '#d97706', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6',
];
